
import CenturyDoomsday from "@/components/CenturyDoomsday.vue";
import Formula from "@/components/Formula.vue";
import YearDoomsday from "@/components/YearDoomsday.vue";
import {
  areResultsCorrect,
  doomsdayMethod,
  Results,
  Weekday,
} from "@/doomsday-method";
import {
  addDays,
  differenceInCalendarDays,
  isAfter,
  isBefore,
  parse,
} from "date-fns";
import { Options, Vue } from "vue-class-component";

@Options({
  components: {
    Formula,
    CenturyDoomsday,
    YearDoomsday,
  },
})
export default class Method extends Vue {
  get inputDate(): string {
    return this._inputDate;
  }

  set inputDate(value: string) {
    this._inputDate = value;
    this.resetResultOptions();
    this.results = doomsdayMethod(this.givenDate);
  }

  results: Results | null = null;

  readonly weekDays = Weekday;
  readonly weekDayNumbers = Object.values(Weekday)
    .filter((key) => Number.isFinite(key))
    .sort() as Weekday[];
  readonly minDateStr = "1600-01-01";
  readonly maxDateStr = "9999-12-31";
  private _inputDate = new Date().toLocaleDateString();
  stepsVisible = true;

  readonly resultOptionsState: Record<
    Weekday,
    "pristine" | "false" | "correct"
  > = Object.fromEntries(
    Object.values(this.weekDayNumbers).map((num) => [num, "pristine"])
  ) as Record<Weekday, "pristine" | "false" | "correct">;

  private readonly formatString = (() => {
    const now = new Date();
    return now
      .toLocaleDateString()
      .replace(now.getFullYear().toString(10), "yyyy")
      .replace((now.getMonth() + 1).toString(10), "MM")
      .replace(now.getDate().toString(10), "dd");
  })();

  randomInputDate(): void {
    this.inputDate = addDays(
      new Date(this.minDateStr),
      Math.floor(
        Math.random() *
          differenceInCalendarDays(
            new Date("2500-12-31"),
            new Date(this.minDateStr)
          )
      )
    ).toLocaleDateString();
  }

  private resetResultOptions(): void {
    Object.keys(this.resultOptionsState).forEach((weekday: string) => {
      this.resultOptionsState[weekday as unknown as Weekday] = "pristine";
    });
  }

  get givenDate(): Date {
    const parsed = parse(this._inputDate, this.formatString, new Date());
    if (
      isBefore(parsed, new Date(this.maxDateStr)) &&
      isAfter(parsed, new Date(this.minDateStr))
    ) {
      return parsed;
    }
    return new Date(Number.NaN);
  }

  isCorrect(results: Results | null): boolean | null {
    return results == null ? null : areResultsCorrect(results);
  }

  format(date: Date): string {
    return new Intl.DateTimeFormat(navigator.language, {
      day: "2-digit",
      month: "2-digit",
    }).format(date);
  }

  abs(value: number): number {
    return Math.abs(value);
  }

  check(weekday: Weekday): void {
    const results = this.results;
    if (results == null) {
      return;
    }
    this.resultOptionsState[weekday] =
      results.result === weekday ? "correct" : "false";
  }

  created(): void {
    this.results = doomsdayMethod(this.givenDate);
  }
}
